
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  Injectable,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { selectAuthenticatedUser } from '@kinet-open-network/client/auth/data-access';
import {
  ApiPaginatedResponse,
  ApiQuery,
} from '@kinet-open-network/client/core/api-client';
import { DiscountCodesService } from '@kinet-open-network/client/discount-codes/data-access';
import { LocationsService } from '@kinet-open-network/client/locations/data-access';
import { ServPackagesService } from '@kinet-open-network/client/serv-packages/data-access';
import { TagsService } from '@kinet-open-network/client/tags/data-access';
import {
  createEnumList,
  IPriceRule,
  IPriceRuleGroup,
  PriceCondition,
  PriceConditionType,
  PriceRuleGroupBoolean,
  priceRuleGroupBooleanOptions,
  PriceRuleType,
  priceRuleTypeOptions,
  serviceTypeOptions,
} from '@kinet-open-network/shared/data-access/model';
import { Store } from '@ngrx/store';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { map, Observable, of, startWith } from 'rxjs';
import {
  fieldGroup,
  formBuilder,
  select,
  selectLazy,
} from '../../builder/field-builder.factory';
import { DynamicFormField } from '../../dynamic-form/dynamic-form.component';

interface PriceConditionProps extends FormlyFieldProps {
  enabledRules?: PriceRuleType[];
}

export interface FormlyPriceConditionsFieldConfig
  extends FormlyFieldConfig<PriceConditionProps> {}

@Component({
  selector: 'kon-price-conditions-group',
  imports: [
    forwardRef(() => PriceConditionsRuleComponent),
    ButtonModule,
    MenuModule,
    FormsModule,
    ButtonGroupModule,
    TooltipModule
],
  template: `
    <div class="border border-gray-200 p-2 rounded">
      <div class="flex justify-between items-center">
        <span class="text-sm text-gray-600 font-medium pl-2 -mt-2">
          Skupina podmienok
        </span>

        <div>
          <button
            pButton
            type="button"
            class="p-button-sm p-button-text p-button-secondary p-button-rounded"
            icon="pi pi-ellipsis-v"
            pTooltip="Možnosti skupiny"
            (click)="menu.toggle($event)"
          ></button>

          <p-menu
            #menu
            [model]="menuModel()"
            [popup]="true"
            [style]="{ width: '240px' }"
            appendTo="body"
          ></p-menu>
        </div>
      </div>

      @for (
        child of group().children;
        track i;
        let i = $index;
        let last = $last
      ) {
        @if (child.type === 'group') {
          <kon-price-conditions-group
            [group]="child"
            (groupChange)="handleChildChange($event, i)"
            (removeGroup)="handleChildRemove(i)"
          />
        } @else {
          <kon-price-conditions-rule
            [rule]="child"
            (ruleChange)="handleChildChange($event, i)"
            (ruleRemove)="handleChildRemove(i)"
          />
        }

        @if (!last) {
          <div class="py-2 pl-2 font-medium text-sm">{{ booleanLabel() }}</div>
        }
      }

      <div class="mt-2">
        <p-buttonGroup>
          <button
            pButton
            type="button"
            icon="pi pi-plus"
            label="Podmienka"
            size="small"
            outlined="true"
            (click)="addRule()"
          ></button>

          <button
            pButton
            type="button"
            icon="pi pi-plus"
            label="Skupina"
            size="small"
            outlined="true"
            (click)="addGroup()"
          ></button>
        </p-buttonGroup>
      </div>
    </div>
  `,
})
export class PriceConditionsGroupComponent {
  readonly group = input.required<IPriceRuleGroup>();
  readonly groupChange = output<IPriceRuleGroup>();
  readonly removeGroup = output<void>();

  readonly booleanLabel = computed(
    () => priceRuleGroupBooleanOptions.get(this.group().boolean)?.label ?? '',
  );

  readonly priceRuleGroupBooleanOptions = priceRuleGroupBooleanOptions;

  readonly menuModel = computed<MenuItem[]>(() => [
    {
      label: 'Zmazať skupinu',
      command: () => this.removeGroup.emit(),
    },
    {
      label:
        'Zmeniť operátor na "' +
        priceRuleGroupBooleanOptions.get(
          this.getNextBoolean(this.group().boolean),
        )?.label +
        '"',
      command: () =>
        this.groupChange.emit({
          ...this.group(),
          boolean: this.getNextBoolean(this.group().boolean),
        }),
      styleClass: 'whitespace-nowrap',
    },
  ]);

  addRule() {
    const group = this.group();

    this.groupChange.emit({
      ...group,
      children: [
        ...group.children,
        {
          type: PriceConditionType.Rule,
          rule: PriceRuleType.HasTag,
          value: null,
        },
      ],
    });
  }

  addGroup() {
    const group = this.group();

    this.groupChange.emit({
      ...group,
      children: [
        ...group.children,
        {
          type: PriceConditionType.Group,
          boolean: PriceRuleGroupBoolean.And,
          children: [],
        },
      ],
    });
  }

  handleChildChange(child: IPriceRule | IPriceRuleGroup, index: number) {
    const group = this.group();

    this.groupChange.emit({
      ...group,
      children: group.children.map((c, i) => (i === index ? child : c)),
    });
  }

  handleChildRemove(index: number) {
    const group = this.group();

    this.groupChange.emit({
      ...group,
      children: group.children.filter((_, i) => i !== index),
    });
  }

  private getNextBoolean(boolean: PriceRuleGroupBoolean) {
    if (boolean === PriceRuleGroupBoolean.And) {
      return PriceRuleGroupBoolean.Or;
    } else {
      return PriceRuleGroupBoolean.And;
    }
  }
}

export interface PriceConditionRuleOption {
  label: string;
  value: any;
}

@Injectable()
export class PriceConditionRuleOptionsDataService {
  private servPackagesService = inject(ServPackagesService);
  private tagsService = inject(TagsService);
  private locationsService = inject(LocationsService);
  private discountCodesService = inject(DiscountCodesService);
  private store = inject(Store);
  private user = this.store.selectSignal(selectAuthenticatedUser);

  public getOptions(
    rule: PriceRuleType,
    q?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<PriceConditionRuleOption>> {
    q = this.prepareQuery(q);

    switch (rule) {
      case PriceRuleType.HasTag:
        return this.getTagsOptions(q);
      case PriceRuleType.HasServPackage:
        return this.getServPackagesOptions(q);
      case PriceRuleType.HasIndividualPrice:
        return this.getIndividualPriceOptions(q);
      case PriceRuleType.HasServPackageType:
        return this.getServPackageTypesOptions(q);
      case PriceRuleType.HasLocation:
        return this.getLocationsOptions(q);
      case PriceRuleType.HasCommitment:
        return this.getCommitmentOptions(q);
      case PriceRuleType.HasCode:
        return this.getDiscountCodesOptions(q);
      default:
        return of(
          this.createPaginatedResponseFromArray<PriceConditionRuleOption>([]),
        );
    }
  }

  private getTagsOptions(
    query?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<PriceConditionRuleOption>> {
    return this.tagsService.query(query).pipe(
      map((res) => ({
        ...res,
        data: res.data.map((tag) => ({
          label: tag.label,
          value: tag.id,
        })),
      })),
    );
  }

  private getServPackagesOptions(
    query?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<PriceConditionRuleOption>> {
    return this.servPackagesService.query(query).pipe(
      map((res) => ({
        ...res,
        data: res.data.map((servPackage) => ({
          label: servPackage.name,
          value: servPackage.id,
        })),
      })),
    );
  }

  private getServPackageTypesOptions(
    query?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<PriceConditionRuleOption>> {
    return of(
      this.createPaginatedResponseFromArray(createEnumList(serviceTypeOptions)),
    );
  }

  private getLocationsOptions(
    query?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<PriceConditionRuleOption>> {
    const user = this.user();

    if (!user) {
      return of(this.createPaginatedResponseFromArray([]));
    }

    return this.locationsService
      .getLocationPermissions(user.groupId, query ?? {})
      .pipe(
        map((res) => ({
          ...res,
          data: res.data.map((location) => ({
            label: location.label,
            value: location.id,
          })),
        })),
      );
  }

  private getDiscountCodesOptions(query?: ApiQuery | null) {
    return this.discountCodesService.query(query).pipe(
      map((res) => ({
        ...res,
        data: res.data.map((discountCode) => ({
          label: discountCode.code,
          value: discountCode.id,
        })),
      })),
    );
  }

  private getCommitmentOptions(
    query?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<PriceConditionRuleOption>> {
    return this.getTrueFalseOptions('Má viazanosť', 'Nemá viazanosť');
  }

  private getIndividualPriceOptions(
    query?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<PriceConditionRuleOption>> {
    return this.getTrueFalseOptions();
  }

  private createPaginatedResponseFromArray<T extends PriceConditionRuleOption>(
    data: T[],
  ): ApiPaginatedResponse<T> {
    return {
      data,
      meta: {
        total: data.length,
        perPage: data.length,
        currentPage: 1,
        from: 1,
        to: data.length,
      },
    };
  }

  private getTrueFalseOptions(trueLabel = 'Áno', falseLabel = 'Nie') {
    return of(
      this.createPaginatedResponseFromArray([
        {
          label: trueLabel,
          value: true,
        },
        {
          label: falseLabel,
          value: false,
        },
      ]),
    );
  }

  private prepareQuery(query?: ApiQuery | null) {
    if (!query) {
      return {};
    }

    delete query.fields;

    return query;
  }
}

@Component({
  selector: 'kon-price-conditions-rule',
  imports: [FormlyModule, ButtonModule, TooltipModule],
  template: `
    <div class="flex items-center gap-2">
      <formly-form
        class="flex-1"
        [fields]="fields()"
        [model]="rule()"
        (modelChange)="handleRuleChange($event)"
      />

      <p-button
        type="button"
        icon="pi pi-times"
        pTooltip="Zmazať podmienku"
        [text]="true"
        [severity]="'danger'"
        [size]="'small'"
        [rounded]="true"
        (click)="removeRule()"
      ></p-button>
    </div>
  `,
  providers: [PriceConditionRuleOptionsDataService],
})
export class PriceConditionsRuleComponent {
  private dataService = inject(PriceConditionRuleOptionsDataService);
  private typeComponent = inject(FormlyPriceConditionsTypeComponent);

  readonly rule = input.required<IPriceRule>();
  readonly ruleChange = output<IPriceRule>();
  readonly ruleRemove = output<void>();

  readonly fields = computed<DynamicFormField[]>(() => {
    const fields = formBuilder(
      fieldGroup()
        .fieldGroupClassName('grid grid-cols-2 gap-4')
        .fieldGroup([
          select('rule')
            .options(this.getRuleTypeOptions())
            .props({
              required: true,
              change: () =>
                this.handleRuleChange({
                  value: null,
                }),
            })
            .wrappers([]),
          selectLazy('value', '', 'label', 'value')
            .props({
              getValueCondition: (value) => `id eq "${value}"`,
            })
            .query({
              query: (q) => this.dataService.getOptions(this.rule().rule, q),
            })
            .wrappers([]),
        ]),
    );

    return fields;
  });

  handleRuleChange(rule: Partial<IPriceRule>) {
    this.ruleChange.emit({
      ...this.rule(),
      ...rule,
    });
  }

  removeRule() {
    this.ruleRemove.emit();
  }

  private getRuleTypeOptions() {
    const enabledRules = this.typeComponent.props.enabledRules;

    if (!enabledRules) {
      return createEnumList(priceRuleTypeOptions);
    }

    return createEnumList(priceRuleTypeOptions).filter((option) =>
      enabledRules.includes(option.value as PriceRuleType),
    );
  }
}

@Component({
  selector: 'kon-price-conditions-type',
  imports: [
    FormlyModule,
    SelectModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    PriceConditionsGroupComponent,
    ButtonModule
],
  template: `
    @if (value(); as condition) {
      <kon-price-conditions-group
        [group]="condition"
        (groupChange)="handleConditionChange($event)"
        (removeGroup)="handleConditionRemove()"
      />
    } @else {
      <button
        pButton
        type="button"
        icon="pi pi-plus"
        label="Pridať podmienky"
        class="p-button-outlined p-button-sm"
        (click)="addRootCondition()"
      ></button>
    }
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyPriceConditionsTypeComponent
  extends FieldType<FieldTypeConfig<PriceConditionProps>>
  implements OnInit
{
  private destroyRef = inject(DestroyRef);

  readonly value = signal<PriceCondition | null>(null);

  ngOnInit() {
    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.formControl.value),
      )
      .subscribe((value) => {
        if (!value) {
          this.value.set(null);
        }

        this.value.set(value as PriceCondition);
      });
  }

  addRootCondition() {
    this.formControl.setValue({
      type: PriceConditionType.Group,
      boolean: PriceRuleGroupBoolean.And,
      children: [],
    });
  }

  handleConditionChange(condition: PriceCondition) {
    this.formControl.setValue(condition);
  }

  handleConditionRemove() {
    this.formControl.setValue(null);
  }
}
